import { useMemo, useCallback } from "react";
import { useTargetsTemplateContext } from "@/modules/targets/context/targets-template.context";
import { Rank } from "@/modules/user/types/index.v2";
import { TargetTemplateType } from "@/modules/targets/types";
import { useFieldArray } from "react-hook-form";

type MonthKey = "m1" | "m2" | "m3";

const monthMap: Record<string, MonthKey> = {
  month1: "m1",
  month2: "m2",
  month3: "m3",
};

export const getMonthKey = (month: string): MonthKey => monthMap[month];

export const createEmptyTarget = () => ({
  m1: 0,
  m2: 0,
  m3: 0,
});

export const useRankTarget = (rank: Rank, targetType: TargetTemplateType) => {
  const { form } = useTargetsTemplateContext();
  const { control, watch } = form;

  const { update, append } = useFieldArray({
    control,
    name: "ranksTargets",
  });

  const ranksTargets = watch("ranksTargets");

  const targetKey =
    targetType === TargetTemplateType.PERSONAL
      ? "personalTarget"
      : "minimumTarget";

  const currentRankTargets = useMemo(() => {
    return (
      ranksTargets.find((rt) => rt.rankUId === rank.uid) ?? {
        rankUId: rank.uid,
        personalTarget: createEmptyTarget(),
        minimumTarget: createEmptyTarget(),
      }
    );
  }, [ranksTargets, rank.uid]);

  const currentTarget = currentRankTargets[targetKey];

  const updateTarget = useCallback(
    (month: string, value: number) => {
      const monthKey = getMonthKey(month);
      const existingIndex = ranksTargets.findIndex(
        (rt) => rt.rankUId === rank.uid
      );

      if (existingIndex >= 0) {
        // Update existing rank target
        const updatedRankTarget = {
          ...ranksTargets[existingIndex],
          [targetKey]: {
            ...ranksTargets[existingIndex][targetKey],
            [monthKey]: value,
          },
        };
        update(existingIndex, updatedRankTarget);
      } else {
        // Add new rank target
        const newRankTarget = {
          rankUId: rank.uid,
          personalTarget: createEmptyTarget(),
          minimumTarget: createEmptyTarget(),
          [targetKey]: {
            ...createEmptyTarget(),
            [monthKey]: value,
          },
        };
        append(newRankTarget);
      }
    },
    [ranksTargets, update, append, rank.uid, targetKey]
  );

  return { currentTarget, updateTarget };
};
